// Pure URL builders + response parsing + metric curation for the CBS StatLine
// OData "Kerncijfers wijken en buurten" table. Kept free of `fetch` so every
// branch is unit-testable; the async wrapper lives in fetchCbs.js.
//
// The buurt code stored on Adresses.Location (PDOK `buurtcode`, e.g. "BU0363AE02")
// is the exact key CBS uses for its WijkenEnBuurten dimension — no translation
// needed. Field keys carry a table-version suffix (_5, _39, …) and change with
// each yearly table, so CBS_KERNCIJFERS_TABLE and CBS_METRIC_GROUPS must be
// updated together when bumping the year.
//
// AI-CONTEXT: See docs/features/adress-external-data.md

const CBS_BASE = 'https://opendata.cbs.nl/ODataApi/odata'
const CBS_KERNCIJFERS_TABLE = '85984NED' // Kerncijfers wijken en buurten 2024
const CBS_KERNCIJFERS_YEAR = 2024

// Curated, grouped subset of the ~120 available fields. Keys are exact to the
// 2024 table. Units drive display formatting in the component.
const CBS_METRIC_GROUPS = [
    {
        group: 'Inwoners',
        metrics: [
            { key: 'AantalInwoners_5', label: 'Aantal inwoners', unit: '' },
            { key: 'Mannen_6', label: 'Mannen', unit: '' },
            { key: 'Vrouwen_7', label: 'Vrouwen', unit: '' },
            { key: 'k_0Tot15Jaar_8', label: '0 tot 15 jaar', unit: '' },
            { key: 'k_15Tot25Jaar_9', label: '15 tot 25 jaar', unit: '' },
            { key: 'k_25Tot45Jaar_10', label: '25 tot 45 jaar', unit: '' },
            { key: 'k_45Tot65Jaar_11', label: '45 tot 65 jaar', unit: '' },
            { key: 'k_65JaarOfOuder_12', label: '65 jaar of ouder', unit: '' },
            { key: 'Bevolkingsdichtheid_34', label: 'Bevolkingsdichtheid', unit: 'inwoners/km²' },
        ],
    },
    {
        group: 'Huishoudens',
        metrics: [
            { key: 'HuishoudensTotaal_29', label: 'Huishoudens totaal', unit: '' },
            { key: 'Eenpersoonshuishoudens_30', label: 'Eenpersoonshuishoudens', unit: '' },
            { key: 'GemiddeldeHuishoudensgrootte_33', label: 'Gemiddelde huishoudensgrootte', unit: '' },
        ],
    },
    {
        group: 'Wonen',
        metrics: [
            { key: 'Woningvoorraad_35', label: 'Woningvoorraad', unit: '' },
            { key: 'GemiddeldeWOZWaardeVanWoningen_39', label: 'Gemiddelde WOZ-waarde', unit: 'x1000euro' },
            { key: 'Koopwoningen_47', label: 'Koopwoningen', unit: '%' },
            { key: 'HuurwoningenTotaal_48', label: 'Huurwoningen', unit: '%' },
            { key: 'InBezitWoningcorporatie_49', label: 'In bezit woningcorporatie', unit: '%' },
        ],
    },
    {
        group: 'Energie',
        metrics: [
            { key: 'GemiddeldAardgasverbruik_55', label: 'Gemiddeld aardgasverbruik', unit: 'm³' },
            { key: 'GemiddeldeElektriciteitslevering_53', label: 'Gemiddelde elektriciteitslevering', unit: 'kWh' },
            { key: 'PercentageWoningenMetStadsverwarming_56', label: 'Woningen met stadsverwarming', unit: '%' },
            { key: 'AardgasvrijeWoningen_57', label: 'Aardgasvrije woningen', unit: '%' },
            { key: 'WoningenMetZonnestroom_59', label: 'Woningen met zonnestroom', unit: '%' },
        ],
    },
    {
        group: 'Inkomen',
        metrics: [
            { key: 'GemiddeldInkomenPerInwoner_78', label: 'Gemiddeld inkomen per inwoner', unit: 'x1000euro' },
        ],
    },
]

const buildKerncijfersUrl = (buurtCode) => {
    const filter = encodeURIComponent(`WijkenEnBuurten eq '${buurtCode}'`)
    return `${CBS_BASE}/${CBS_KERNCIJFERS_TABLE}/TypedDataSet?$filter=${filter}&$format=json`
}

const parseKerncijfersResponse = (json) => {
    const rows = json && Array.isArray(json.value) ? json.value : []
    return rows.length > 0 ? rows[0] : null
}

// CBS suppresses small-count cells (returned as null). Keep a genuine 0 — it's a
// real statistic — but drop null / undefined / blank strings.
const hasValue = (value) => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string' && value.trim() === '') return false
    return true
}

const cleanValue = (value) => (typeof value === 'string' ? value.trim() : value)

const selectCbsMetrics = (row) => {
    if (!row) return []
    return CBS_METRIC_GROUPS.map((group) => ({
        group: group.group,
        metrics: group.metrics
            .filter((m) => hasValue(row[m.key]))
            .map((m) => ({ key: m.key, label: m.label, unit: m.unit, value: cleanValue(row[m.key]) })),
    })).filter((group) => group.metrics.length > 0)
}

export {
    CBS_BASE,
    CBS_KERNCIJFERS_TABLE,
    CBS_KERNCIJFERS_YEAR,
    CBS_METRIC_GROUPS,
    buildKerncijfersUrl,
    parseKerncijfersResponse,
    selectCbsMetrics,
}
